import { useEffect, useState } from "react";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Typography, CircularProgress, Button } from "@mui/material";
import { toast } from "@rubi/ui";
import { useNavigate, useParams } from "react-router";

import { getSSODetails, ssoStart } from "@/api/apiUserClient";
import {
  useCommunitiesQuery,
  useSsoConfigurations,
} from "@/hooks/useCommunities";

import styles from "./Home.module.css";
import LoginWrapper from "./LoginWrapper";

export const ServiceLogin = () => {
  const { organizationSlug } = useParams();
  const navigate = useNavigate();
  const ssoConfigurations = useSsoConfigurations();
  const organizationConfig = ssoConfigurations.find(
    (config) => config.ssoSlug === organizationSlug,
  );
  const [ssoID, setSSOID] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const {
    data: directory,
    isLoading: directoryLoading,
    isFetching: directoryFetching,
    refetch: refetchDirectory,
  } = useCommunitiesQuery();
  // DB-only SSO slugs aren't in the hardcoded fallback, and a region outage
  // leaves them out of the directory, so only a complete directory proves the
  // slug unknown.
  const organizationNotFound =
    !!organizationSlug && !organizationConfig && !!directory?.complete;

  useEffect(() => {
    if (organizationNotFound) {
      navigate("/login");
    }
  }, [organizationNotFound, navigate]);

  useEffect(() => {
    const loadSSORedirect = async () => {
      const slugToUse = organizationSlug!;

      const result = await getSSODetails(slugToUse);
      const connections = result.sso_connections;
      const connectionIndex = 0;

      const mainConnection = connections[connectionIndex];

      setSSOID(mainConnection.connection_id);
    };
    if (organizationConfig?.ssoSlug) {
      loadSSORedirect();
    }
  }, [organizationConfig]);

  if (organizationSlug && !organizationConfig) {
    if (organizationNotFound) {
      return <div>Organization not found</div>;
    }
    if (directoryLoading) {
      return (
        <LoginWrapper>
          <CircularProgress />
        </LoginWrapper>
      );
    }
    return (
      <LoginWrapper>
        <div className={styles.paper}>
          <Typography component="h1" variant="h5">
            We couldn&apos;t load your organization&apos;s sign-in
          </Typography>
          <p>Please try again in a moment.</p>
          <Button
            name="Retry"
            variant="contained"
            color="primary"
            size="large"
            disabled={directoryFetching}
            fullWidth
            onClick={() => {
              refetchDirectory();
            }}
          >
            {directoryFetching && <CircularProgress size={25} thickness={2} />}
            {"Try again"}
          </Button>
          <Button
            name="Back"
            color="primary"
            startIcon={<ArrowBackIcon />}
            onClick={() => {
              navigate("/login");
            }}
          >
            {"Back"}
          </Button>
        </div>
      </LoginWrapper>
    );
  }

  const handleSubmit = async () => {
    setLoading(true);
    const result = await ssoStart(ssoID);
    if (result.redirect_url) {
      window.location.href = result.redirect_url;
    } else {
      toast.error(
        "There was a problem with the SSO redirect. Please try again.",
      );
    }
    setLoading(false);
  };

  return (
    <LoginWrapper>
      <div className={styles.paper}>
        <div style={{ width: "100%", alignItems: "left" }}>
          <Button
            name="Back"
            color="primary"
            startIcon={<ArrowBackIcon />}
            size="large"
            onClick={() => {
              navigate("/login");
            }}
          >
            {"Back"}
          </Button>
        </div>

        <img
          className={styles.loginLogo}
          src={organizationConfig?.logoURL}
          alt="logo"
        />

        <Typography component="h1" variant="h5">
          Sign in with your {organizationConfig?.fullName} account
        </Typography>
        <p>
          <i>
            You will be redirected to your organization&apos;s typical sign-in
            process
          </i>
        </p>
        <Button
          name="Submit"
          variant="contained"
          type="submit"
          color="primary"
          size="large"
          disabled={!ssoID}
          fullWidth
          onClick={() => {
            handleSubmit();
          }}
        >
          {loading && <CircularProgress size={25} thickness={2} />}
          {"Sign In"}
        </Button>
      </div>
    </LoginWrapper>
  );
};
